/* AZ & CO.

   Four jobs: make the paper, keep the bag, search the catalogue, and switch
   variants on a product page. Everything else is CSS.

   The bag is the important one. Shopify's checkout is closed to us on every
   plan, so the drawer is the last surface we control, and it is also what
   makes the layering page possible: adding a second bottle can no longer
   throw you off the page you are shopping. */

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

/* --------------------------------------------------------------- money */

function money (cents) {
  // One rule everywhere: "AED 660". The shop's own format renders
  // "Dhs. 440.00", which is neither what the design says nor what these
  // prices need, since none of them have decimals.
  const n = Math.round(cents / 100).toLocaleString('en-US');
  const label = (window.AZ && window.AZ.currency) || 'AED';
  return label + ' ' + n;
}

/* ---------------------------------------------------------------- paper

   Generated per session rather than shipped as an image: a stock noise
   texture reads as a stock noise texture. Fine speckle, then a horizontal
   blur that wraps at the tile edge so 220px repeats leave no seam. */

function makeGrain (size = 220) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(size, size);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = 128 + (Math.random() - 0.5) * 26;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 16;
  }
  ctx.putImageData(img, 0, 0);
  // Set the variable the stylesheet expects, not backgroundImage directly:
  // .paper layers two gradients over the grain and only the grain repeats.
  // Assigning backgroundImage wiped both and left one untiled square in the
  // corner of the page.
  document.documentElement.style.setProperty('--grain', `url(${c.toDataURL()})`);
}

/* ---------------------------------------------------------------- reveal

   The hero lines sit translated below their own clipping mask until this
   marks them in. The old site did it and the port dropped it, which left the
   headline invisible: the single worst kind of bug, because the page looks
   deliberately empty rather than broken. */

function initReveal () {
  const sets = $$('.line-set, .hook-object');
  if (!sets.length) return;
  // a frame first, so the transition has a start state to move from
  requestAnimationFrame(() => {
    requestAnimationFrame(() => sets.forEach((el) => el.setAttribute('data-in', '')));
  });
}

/* ------------------------------------------------------------------ bag */

/* v16, loading. The rules this file follows, so the next person can keep
   them:
     - something on screen moves within 100 ms of every press
     - a loader (skeleton, spinner) does not appear for the first 250 ms, and
       once it has appeared it stays for at least 350 ms, so a fast answer
       never makes it flash
     - anything that enters takes 220 to 340 ms, ease-out; anything that
       leaves takes 200 to 250 ms
     - nothing animates under prefers-reduced-motion */

const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const idle = (fn, ms) => (window.requestIdleCallback
  ? requestIdleCallback(fn, { timeout: ms })
  : setTimeout(fn, Math.min(ms, 1200)));

function lateLoader (show, hide) {
  let shownAt = 0;
  const t = setTimeout(() => { shownAt = performance.now(); show(); }, 250);
  return (then) => {
    clearTimeout(t);
    if (!shownAt) return then();
    const left = 350 - (performance.now() - shownAt);
    const finish = () => { hide(); then(); };
    if (left > 0) setTimeout(finish, left); else finish();
  };
}

/* The cart is read twice a page at most. Shopify returns Liquid's copy of the
   cart inside the add reply (section az-cart-state), which is the same data
   as /cart.js, so an add is one request. */
function cartFromSections (reply) {
  try {
    const html = reply && reply.sections && reply.sections['az-cart-state'];
    const m = html && html.match(/<script[^>]*type="application\/json"[^>]*>([\s\S]*?)<\/script>/);
    const c = m && JSON.parse(m[1]);
    if (!c || !Array.isArray(c.items) || typeof c.item_count !== 'number' || typeof c.total_price !== 'number') return null;
    // Liquid prints the picture address without "https:"; /cart.js includes it
    c.items.forEach((it) => {
      if (typeof it.image === 'string' && it.image.slice(0, 2) === '//') it.image = 'https:' + it.image;
    });
    return c;
  } catch (e) { return null; }
}

const Cart = {
  state: null,
  warmed: null,

  async get () {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('cart ' + r.status);
    const c = await r.json();
    this.state = c;
    return c;
  },

  /* Started when a finger lands on the bag icon. The click that follows a
     moment later takes the answer, so it is used once and never goes stale. */
  warm () {
    const now = performance.now();
    if (this.warmed && now - this.warmed.at < 1200) return this.warmed.p;
    const p = this.get().catch(() => null);
    this.warmed = { at: now, p };
    return p;
  },

  take () {
    const w = this.warmed;
    this.warmed = null;
    if (w && performance.now() - w.at < 1200) return w.p;
    return this.get().catch(() => null);
  },

  /* Takes the form, not an id.

     The first version read a single hidden input and posted JSON. That broke
     the moment a form had anything else in it: the gift card carries the
     recipient's name, email, message and send date as line-item properties,
     and every one of them was silently dropped. Its amount is a set of radio
     buttons too, and querying [name=id] returned the first rather than the
     checked one, so every card added was AED 100 whatever you picked.

     FormData carries whatever the form actually holds.

     v16: asks for the cart back in the same reply. If the reply has no
     usable copy, it falls back to reading /cart.js as before. */
  async add (form) {
    const body = new FormData(form);
    body.append('sections', 'az-cart-state');
    const r = await fetch(window.AZ.routes.cart_add + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      const e = new Error(err.description || err.message || 'That could not be added.');
      e.reason = err.description || '';
      throw e;
    }
    const cart = cartFromSections(await r.json().catch(() => null));
    if (cart) { this.state = cart; return cart; }
    return this.get();
  },

  async change (key, qty) {
    const r = await fetch(window.AZ.routes.cart_change + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    // a refused change comes back as {"description": ...} with no items, and
    // painting that used to throw halfway through the drawer
    if (!r.ok) throw new Error('change ' + r.status);
    const c = await r.json();
    if (!c || !Array.isArray(c.items)) throw new Error('change shape');
    this.state = c;
    return c;
  }
};

/* The drawer builds its lines from cart.js, so the two things the Liquid
   snippets do server-side have to be done again here or they simply do not
   happen in the bag. */

function esc (v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* Mirror of snippets/clean-title.liquid. Titles arrive as "Hacivat — Nishane"
   and the drawer already prints the house on the line above, so without this
   every bag line named the house twice.

   The comparison is folded rather than literal because the vendor field on
   this catalogue is a machine slug: "essential_parfums", "memo_paris",
   "etat_libre". A plain startsWith never matched any of them, so 49 bottles
   across 9 houses printed the house inside the name and again underneath it.
   Folding lowercases, drops accents and treats punctuation as a space, which
   also catches "Les Indémodables" against a vendor of "indemodables" — the
   article belongs to the house, so it goes with it. */

function foldName (s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function cleanTitle (title, vendor) {
  let t = String(title || '').trim();
  for (const sep of [' — ', ' – ', ' - ']) {
    if (t.includes(sep)) { t = t.split(sep)[0].trim(); break; }
  }

  const v = foldName(vendor);
  if (v) {
    const vWords = v.split(' ').length;
    const words = t.split(/\s+/);
    const article = ['les', 'le', 'la', 'the'].includes(foldName(words[0])) ? 1 : 0;
    for (const start of article ? [0, 1] : [0]) {
      if (foldName(words.slice(start, start + vWords).join(' ')) !== v) continue;
      const rest = words.slice(start + vWords).join(' ').trim();
      if (rest) t = rest;
      break;
    }
  }

  if (String(title || '').toLowerCase().includes('tester')) t += ' (Tester)';
  return t;
}

/* Adding a bottle changed a number in the corner and slid a panel in. Neither
   of those is announced, so on a screen reader the tap did nothing at all. */
function announce (msg) {
  const region = $('#cart-live');
  if (region) region.textContent = msg;
}

/* What the drawer is showing, as text. A fresh answer from the server that
   says the same thing is not worth a repaint. */
let paintedSig = '';
const sigOf = (c) => c.total_price + '|' + c.items.map((i) => i.key + ':' + i.quantity).join(',');
const EMPTY = { items: [], item_count: 0, total_price: 0 };
// azco-qty.js changed a quantity and drew it itself; the drawer now shows this
document.addEventListener('az:synced', (e) => { if (e.detail && e.detail.items) paintedSig = sigOf(e.detail); });

function paintCart (cart) {
  const lines = $('#cart-lines');
  const foot = $('#cart-foot');
  const empty = $('#cart-empty');
  const count = $('#bag-count');

  paintedSig = sigOf(cart);

  if (count) {
    count.textContent = cart.item_count;
    count.hidden = cart.item_count === 0;
  }
  if (!lines) return;
  lines.removeAttribute('aria-busy');

  if (!cart.items.length) {
    lines.innerHTML = '';
    if (foot) foot.hidden = true;
    if (empty) empty.hidden = false;
    return;
  }
  if (empty) empty.hidden = true;
  if (foot) foot.hidden = false;

  lines.innerHTML = cart.items.map((it) => `
    <div class="bag-line" data-key="${it.key}">
      <a class="bag-plate" href="${it.url}">
        ${it.image ? `<img src="${it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}"
          width="70" height="94" alt="" loading="lazy">` : ''}
      </a>
      <div class="bag-id">
        <p class="bag-house">${esc(it.vendor || '')}</p>
        <a class="bag-name" href="${it.url}">${esc(cleanTitle(it.product_title, it.vendor))}</a>
        ${it.variant_title && it.variant_title !== 'Default Title'
          ? `<p class="bag-variant">${esc(it.variant_title)}</p>` : ''}
        <button type="button" class="bag-remove" data-remove="${it.key}">Remove</button>
      </div>
      <p class="bag-price">${money(it.final_line_price)}</p>
    </div>`).join('');

  const total = $('#cart-total');
  if (total) total.textContent = money(cart.total_price);

  paintSaved(cart);
}

/* One place that learns about a new cart. The event goes first so azco-qty.js
   holds the new numbers before its observer repaints the lines. */
function useCart (cart) {
  if (window.AZ) window.AZ.cart_count = cart.item_count;
  document.dispatchEvent(new CustomEvent('az:cart', { detail: cart }));
  paintCart(cart);
}

/* Two grey rows the size of real ones, for a bag that is slow to answer. */
function skeletonLines (n) {
  return Array.from({ length: n }, () => `
    <div class="bag-line bag-line--skel" aria-hidden="true">
      <span class="bag-plate"><i class="skel-plate"></i></span>
      <div class="bag-id"><i class="skel-bar skel-bar--s"></i><i class="skel-bar"></i><i class="skel-bar skel-bar--s"></i></div>
      <i class="skel-bar skel-bar--price"></i>
    </div>`).join('');
}

/* ------------------------------------------------------- saved vs retail

   cart.js does not carry compare_at_price. Its original_price field looks
   like the retail figure and is not: it only ever reflects Shopify's own
   cart discounts, so on this shop it equals price on every line and would
   have printed a saving of zero forever.

   The retail number lives on the product JSON instead, one request per
   distinct handle in the bag, cached for the life of the page. A bag holds
   one or two bottles, so this is a request or two, and the row simply stays
   hidden if any of it fails — a wrong saving is worse than no saving. */

const retailCache = new Map();

async function variantRetail (handle) {
  if (!retailCache.has(handle)) {
    retailCache.set(handle, fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        const map = new Map();
        if (p && p.variants) p.variants.forEach((v) => map.set(v.id, v.compare_at_price));
        return map;
      })
      .catch(() => new Map()));
  }
  return retailCache.get(handle);
}

async function paintSaved (cart) {
  const row = $('#cart-saved');
  const out = $('#cart-saved-value');
  if (!row || !out) return;

  try {
    const maps = await Promise.all([...new Set(cart.items.map((i) => i.handle))].map(variantRetail));
    const byHandle = new Map([...new Set(cart.items.map((i) => i.handle))].map((h, n) => [h, maps[n]]));

    let saved = 0;
    for (const it of cart.items) {
      const retail = (byHandle.get(it.handle) || new Map()).get(it.variant_id);
      // guard the known data problem: a few bottles carry a compare-at at or
      // below the selling price, and those must not subtract from the total
      if (retail && retail > it.final_price) saved += (retail - it.final_price) * it.quantity;
    }

    if (saved > 0) {
      out.textContent = money(saved);
      row.hidden = false;
    } else {
      row.hidden = true;
    }
  } catch {
    row.hidden = true;
  }
}

/* ------------------------------------------------------------- undo

   Removing is one tap beside a price on a phone, and the line above is
   another product. A confirm dialog would tax every correct removal to catch
   the occasional wrong one, and it is the kind of dialog people learn to
   dismiss without reading. This costs nothing unless it is needed.

   The window is deliberately generous: eight seconds is long enough to
   notice the bag is now wrong, which is the moment the mistake registers.

   v16: the offer appears on the press, not when the server has answered
   (628 ms later). Undo waits for the remove request before it adds the
   bottle back, so the two can never cross. */

let undoSnap = null;
let undoTimer = null;

function offerUndo (snap) {
  const bar = $('#bag-undo');
  const text = $('#bag-undo-text');
  if (!bar || !text) return;

  undoSnap = snap;
  text.textContent = snap.name + ' removed.';
  bar.hidden = false;
  announce(snap.name + ' removed from your bag.');

  clearTimeout(undoTimer);
  undoTimer = setTimeout(hideUndo, 8000);
}

function hideUndo () {
  const bar = $('#bag-undo');
  clearTimeout(undoTimer);
  undoSnap = null;
  if (bar) bar.hidden = true;
}

async function takeUndo () {
  if (!undoSnap) return;
  const snap = undoSnap;
  hideUndo();
  if (snap.gone) {
    // the remove may still be in the air; the add goes after it, never beside it
    try { await snap.gone; } catch (e) { return; }   // it failed, so the bottle never left
  }
  /* JSON rather than FormData here: the properties arrive as an object
     already, so there is no form to read and nothing to drop. */
  let cart = null;
  try {
    const r = await fetch(window.AZ.routes.cart_add + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: snap.id, quantity: snap.quantity, properties: snap.properties }],
        sections: 'az-cart-state'
      })
    });
    if (r.ok) cart = cartFromSections(await r.json().catch(() => null));
  } catch (e) { cart = null; }
  if (!cart) cart = await Cart.get().catch(() => null);
  if (!cart) return;
  Cart.state = cart;
  useCart(cart);
  announce(snap.name + ' put back.');
}

/* ----------------------------------------------------------------- drawer */

let drawerFrom = null;
let closing = null;

function openDrawer () {
  const d = $('#cart-drawer');
  if (!d) return;
  clearTimeout(closing);
  d.classList.remove('is-closing');
  if (d.hidden && !drawerFrom) drawerFrom = document.activeElement;
  d.hidden = false;
  [...document.body.children].forEach((n) => { if (n !== d) n.setAttribute('inert', ''); });
  document.documentElement.style.overflow = 'hidden';
  const close = $('.drawer-close', d);
  if (close) close.focus({ preventScroll: true });
}

/* Leaves in 210 ms (ease-in) and then hides. The page behind wakes at once,
   so nothing waits on the animation. Focus goes back to what opened it. */
function closeDrawer (now) {
  const d = $('#cart-drawer');
  if (!d || d.hidden) return;
  hideUndo();
  [...document.body.children].forEach((n) => n.removeAttribute('inert'));
  document.documentElement.style.overflow = '';
  const back = drawerFrom;
  drawerFrom = null;
  const finish = () => {
    d.hidden = true;
    d.classList.remove('is-closing');
    if (back && back.isConnected && !back.disabled && back.focus) back.focus({ preventScroll: true });
  };
  clearTimeout(closing);
  if (now === true || reduced()) return finish();
  d.classList.add('is-closing');
  closing = setTimeout(finish, 210);
}

/* The bag opens on the press. It paints from the last cart we know about;
   the server's answer only changes the screen if it disagrees. */
async function openBag () {
  openDrawer();

  const lines = $('#cart-lines');
  const count = window.AZ ? window.AZ.cart_count : 0;
  const drawn = !!paintedSig;

  // a cart we already hold but have not drawn yet: draw it now
  if (!drawn && Cart.state) useCart(Cart.state);

  // nothing in it: no request is needed to draw that, the check runs behind
  if (!drawn && !Cart.state && !count) {
    paintCart(EMPTY);
    Cart.take().then((c) => {
      if (c && c.item_count > 0 && !(window.AZ.cartBusy && window.AZ.cartBusy())) useCart(c);
    });
    return;
  }

  // something in it and nothing to draw yet: grey rows, but only if it is slow
  let stop = null;
  const unknown = !paintedSig;
  if (unknown && lines) {
    lines.setAttribute('aria-busy', 'true');
    announce('Opening your bag');
    stop = lateLoader(
      () => { if (!paintedSig) { lines.dataset.skel = '1'; lines.innerHTML = skeletonLines(2); } },
      () => { delete lines.dataset.skel; }
    );
  }

  const cart = await Cart.take();

  const apply = () => {
    if (lines) lines.removeAttribute('aria-busy');
    if (!cart) {
      if (unknown && lines) {
        lines.innerHTML = '<p class="bag-oops">We could not open your bag just now. Close it and try again.</p>';
        announce('We could not open your bag just now.');
      }
      return;
    }
    if (window.AZ.cartBusy && window.AZ.cartBusy()) return;   // a quantity press is still in the air
    if (sigOf(cart) !== paintedSig) useCart(cart);
  };
  if (stop) stop(apply); else apply();
}

/* ------------------------------------------------------------ add button

   Pressed, pending and failed. The button keeps its width while it says
   Adding (it used to shrink by a third), and a failed add puts the button
   back and says why, under it, instead of leaving a dead grey button. */

const WA = () => (window.AZ && window.AZ.whatsapp) || '971509301032';
let pressId = 0;

function bottleOf (form) {
  const card = form.closest('.pdp-top, .spec, .rail-cell, .cab-cell, .buybar, .azs-p, li');
  if (card && card.dataset.name) return card.dataset.name;   // a search row carries its name
  const el = card && card.querySelector('.pdp-name, .spec-name, .rail-name, .cab-name, .buybar-name');
  return el ? el.textContent.trim().replace(/\s+/g, ' ') : '';
}

// the product page disables its button for a sold-out size; put that back
function unavailable (btn) {
  if (btn.id !== 'pdp-cart') return false;
  const pick = $('#pdp-variants .pick[data-on]');
  return !!pick && pick.dataset.available !== 'true';
}

function clearNote (form) {
  const n = form.querySelector('.add-note');
  if (n) n.remove();
}

function note (form, text, ask) {
  let p = form.querySelector('.add-note');
  if (!p) {
    p = document.createElement('p');
    p.className = 'add-note';
    const btn = form.querySelector('button[type=submit]');
    // under the button and above Shopify's own payment button
    if (btn && btn.parentNode === form) btn.insertAdjacentElement('afterend', p); else form.appendChild(p);
  }
  p.textContent = text;
  p.setAttribute('role', ask ? 'alert' : 'status');
  if (ask) {
    const a = document.createElement('a');
    const who = bottleOf(form);
    a.href = 'https://wa.me/' + WA() + '?text=' + encodeURIComponent(
      'Hi AZ & CO, I tried to add ' + (who || 'a bottle') + " to my bag and it didn't go through.");
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = 'Ask on WhatsApp';
    p.append(' ', a);
  }
  return p;
}

function pressAdd (form, btn) {
  const id = String(++pressId);
  // v17: buttons carry markup now (the pill's price and chip), so the label
  // is kept and put back as HTML, and only the chip says Adding
  const label = btn ? btn.innerHTML : '';
  const wide = btn ? Math.ceil(btn.getBoundingClientRect().width) : 0;
  let slowT = 0;
  let longT = 0;

  form.dataset.azBusy = id;
  clearNote(form);
  // focus goes back to this button when the bag closes (a disabled button cannot hold it meanwhile).
  // A form marked data-cart-stay (the search panel) never opens the bag, so it hands nothing over.
  const bag = $('#cart-drawer');
  if (btn && bag && bag.hidden && !form.hasAttribute('data-cart-stay')) drawerFrom = btn;
  if (btn) {
    btn.dataset.azLabel = label;
    btn.style.minWidth = wide + 'px';        // measured before the words change
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    const chip = btn.querySelector('.pill-c');
    if (chip) chip.textContent = btn.dataset.adding || 'Adding';
    else btn.textContent = btn.dataset.adding || 'Adding';
  }

  const give = () => {                       // the button comes back
    if (form.dataset.azBusy !== id) return;
    delete form.dataset.azBusy;
    if (!btn) return;
    btn.removeAttribute('aria-busy');
    btn.style.minWidth = '';
    btn.innerHTML = btn.dataset.azLabel || label;
    delete btn.dataset.azLabel;
    btn.disabled = unavailable(btn);
  };
  const stopTimers = () => { clearTimeout(slowT); clearTimeout(longT); };

  slowT = setTimeout(() => {
    note(form, 'Still adding…');
    announce('Still adding…');
  }, 4500);
  longT = setTimeout(() => {
    // the request is NOT cancelled: the server may still add it, and if it
    // does the bag repaints and this message goes
    const t = 'This is taking longer than it should. Try again, or ask us on WhatsApp.';
    note(form, t, true);
    announce(t);
    give();
  }, 12000);

  return {
    done () { stopTimers(); give(); clearNote(form); },
    fail (err) {
      stopTimers();
      give();
      if (bag && bag.hidden) {
        drawerFrom = null;   // the bag never opened; nothing to hand focus back to
        // the button was disabled while it worked, which drops focus to the page; put it back
        if (btn && (!document.activeElement || document.activeElement === document.body)) btn.focus({ preventScroll: true });
      }
      const why = err && err.reason ? ' ' + err.reason : '';
      const t = "That didn't go into your bag." + why;
      note(form, t, true);
      announce(t);
    }
  };
}

/* Everything an old page was waiting on stops being true when the browser
   puts it back from the back/forward cache. */
function resetPage () {
  $$('[data-az-label]').forEach((b) => {
    b.innerHTML = b.dataset.azLabel;
    delete b.dataset.azLabel;
    b.removeAttribute('aria-busy');
    b.style.minWidth = '';
    b.disabled = unavailable(b);
  });
  $$('[data-az-busy]').forEach((f) => { delete f.dataset.azBusy; });
  $$('.add-note').forEach((n) => n.remove());
  hideUndo();
  closeDrawer(true);
  if (window.AZsearch && window.AZsearch.close) window.AZsearch.close();
  [...document.body.children].forEach((n) => n.removeAttribute('inert'));
  document.documentElement.style.overflow = '';
  document.dispatchEvent(new CustomEvent('az:reset'));
}

let changeSeq = 0;

function initCart () {
  window.AZ.drawer = { open: openDrawer, close: closeDrawer };

  /* v22 (22 Oct 2026): one tap, one add-to-bag event. Shopify's tracker
     listens on every /cart/add form and counts the submit unless it is
     already defaultPrevented, and it counts the /cart/add.js fetch below as
     well, so each tap reached Shopify Analytics, Meta and Snapchat twice.
     Taking the default here, in the capture phase before the form's own
     listeners run, leaves only the fetch count. It lives in this file so
     that if theme.js never runs the form still posts natively. */
  document.addEventListener('submit', (e) => {
    if (e.target.closest && e.target.closest('[data-cart-add]')) e.preventDefault();
  }, true);

  // every add-to-bag on the site posts through here instead of navigating
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-add]');
    if (!form) return;
    e.preventDefault();
    if (form.dataset.azBusy) return;           // a second press while one is in the air
    const btn = form.querySelector('button[type=submit]');
    const press = pressAdd(form, btn);
    try {
      const cart = await Cart.add(form);
      press.done();
      useCart(cart);
      const last = cart.items[cart.items.length - 1];
      announce(last
        ? cleanTitle(last.product_title, last.vendor) + ' added to your bag. '
          + cart.item_count + (cart.item_count === 1 ? ' item.' : ' items.')
        : 'Added to your bag.');
      // the search panel adds in place: it stays open and says so itself (az:added)
      if (!form.hasAttribute('data-cart-stay')) openDrawer();
      /* Anything that needs to know its own add landed — the gift form wants
         to confirm the recipient details it just captured, which the drawer
         cannot do because a line item does not show them. */
      document.dispatchEvent(new CustomEvent('az:added', { detail: { form, cart } }));
    } catch (err) {
      console.error(err);
      press.fail(err);
    }
  });

  document.addEventListener('pointerdown', (e) => {
    if (e.target.closest && e.target.closest('#cart-open')) Cart.warm();
  }, { passive: true });

  document.addEventListener('click', async (e) => {
    if (e.target.closest('#cart-open')) return openBag();
    if (e.target.closest('[data-cart-close]')) return closeDrawer();
    const rm = e.target.closest('[data-remove]');
    if (rm) {
      /* Snapshot before it goes. cart.js hands back the variant, the
         quantity and the line properties, which is everything needed to put
         it back exactly — including a gift card's recipient name, number and
         send date, none of which anyone wants to type twice. The quantity is
         read off the line itself, because the stepper changes it without
         telling this copy of the cart. */
      const key = rm.dataset.remove;
      const line = ((Cart.state && Cart.state.items) || []).find((i) => i.key === key);
      const shown = rm.closest('.bag-line') && rm.closest('.bag-line').querySelector('[data-qty-n]');
      const snap = line && {
        id: line.variant_id,
        quantity: (shown && parseInt(shown.textContent, 10)) || line.quantity,
        properties: line.properties || {},
        name: cleanTitle(line.product_title, line.vendor)
      };
      const mine = ++changeSeq;
      const gone = Cart.change(key, 0);
      if (snap) { snap.gone = gone; offerUndo(snap); }
      try {
        const cart = await gone;
        if (mine === changeSeq) useCart(cart);   // a newer remove will draw its own answer
      } catch (err) {
        hideUndo();
        document.dispatchEvent(new CustomEvent('az:restore'));
        announce("We couldn't remove that. Your bag now shows what is in it.");
        Cart.get().then(useCart).catch(() => {});
      }
    }
    if (e.target.closest('#bag-undo-do')) return takeUndo();
  });

  document.addEventListener('keydown', (e) => {
    const d = $('#cart-drawer');
    if (e.key === 'Escape' && d && !d.hidden) closeDrawer();
  });

  const refresh = () => Cart.get().then(useCart).catch(() => {});

  // back/forward cache: the page comes back as it was left, pending states and all
  addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    resetPage();
    refresh();
  });
  // a prerendered page becomes the visible one: its bag was last read when it was built
  document.addEventListener('prerenderingchange', refresh);

  if (window.AZ.cart_count > 0 && !document.prerendering) {
    idle(() => {
      Cart.get().then((c) => {
        const l = $('#cart-lines');
        if (!paintedSig && !(l && l.dataset.skel)) useCart(c);   // not under the grey rows; the bag paints after them
      }).catch(() => {});
    }, 2500);
  }
}

/* --------------------------------------------------------------- search

   v16: the search lives in assets/azco-search.js (panel #az-search). The old
   catalogue download (/products.json, 2.5 MB) and its dropdown were removed
   from this file with the loading work; nothing here calls them any more. */

/* -------------------------------------------------------- variant picker

   Price, saving, instalment, the concentration line and the hidden id all
   move together. Nothing on the page is allowed to disagree with the button.

   v14: the struck price carries its "Boutique price" label from the
   boutique-price snippet, so only the amount (#pdp-was-v) is rewritten here
   and no percentage is printed. Testers show it too. The tester line follows the picked option,
   and so do the sticky bar's price and the WhatsApp button's message. */

/* The floating WhatsApp button (#ask-float, theme.liquid) opens a chat that
   already names the bottle, so the first message is a question about it and
   not "which one?". Built from the data-ask attributes on .pdp-buy; the
   button keeps its plain link everywhere else. Plain "AED 1,450" rather
   than money(), whose no-break space would arrive as a stray character in
   some WhatsApp clients. */
function paintAsk (kind, price, variant) {
  const a = $('#ask-float');
  const buy = $('.pdp-buy[data-ask-name]');
  if (!a || !buy) return;
  if (!a.dataset.base) a.dataset.base = a.getAttribute('href').split('?')[0];
  const label = (window.AZ && window.AZ.currency) || 'AED';
  const bits = [buy.dataset.askName];
  if (kind && kind !== 'Default Title') bits.push(kind);
  if (price > 0) bits.push(label + ' ' + Math.round(price / 100).toLocaleString('en-US'));
  const url = buy.dataset.askUrl + (variant ? '?variant=' + variant : '');
  const text = "Hi AZ & CO, I'm looking at " + bits.join(', ') + ': ' + url;
  a.href = a.dataset.base + '?text=' + encodeURIComponent(text);
  a.setAttribute('aria-label', 'Ask about ' + buy.dataset.askName + ' on WhatsApp');
}

function initVariants () {
  const buy = $('.pdp-buy[data-ask-name]');
  if (buy) paintAsk(buy.dataset.askKind, Number(buy.dataset.askPrice), buy.dataset.askVariant);

  const picker = $('#pdp-variants');
  if (!picker) return;

  picker.addEventListener('click', (e) => {
    const b = e.target.closest('.pick');
    if (!b) return;
    $$('.pick', picker).forEach((x) => {
      x.removeAttribute('data-on');
      x.setAttribute('aria-checked', 'false');
    });
    b.setAttribute('data-on', '');
    b.setAttribute('aria-checked', 'true');

    const price = Number(b.dataset.price);
    const compare = Number(b.dataset.compare);
    const id = $('#pdp-variant-id');
    if (id) id.value = b.dataset.variant;

    const now = $('#pdp-now');
    if (now) now.textContent = money(price);

    const kind = $('#pdp-kind');
    if (kind) kind.textContent = b.dataset.kind;

    const was = $('#pdp-was');
    const save = $('#pdp-save');
    /* Same floor the server applies. Without it, switching variant could
       resurrect a 1% struck price that Liquid had deliberately withheld on
       load, so the page would contradict itself on a tap. Testers show the
       struck price and the saving like any other variant. */
    const floor = Number((was && was.dataset.floor) || 0);
    const pct = compare > price ? Math.round((compare - price) * 100 / compare) : 0;
    const has = compare > price && pct >= floor;
    if (was) {
      was.hidden = !has;
      const amount = $('#pdp-was-v');
      if (has && amount) amount.textContent = money(compare);
    }
    if (save) {
      save.hidden = !has;
      if (has) save.textContent = `${save.dataset.lead || 'You save'} ${money(compare - price)}.`;
    }

    const tester = $('#pdp-tester');
    if (tester) tester.hidden = b.dataset.tester !== 'true';

    const barPrice = $('#buybar-price');
    if (barPrice) barPrice.textContent = money(price);

    paintAsk(b.dataset.kind, price, b.dataset.variant);

    const cart = $('#pdp-cart');
    if (cart) cart.disabled = b.dataset.available !== 'true';

    /* v27: a sold-out size gets no delivery promise and no Apple Pay (the
       CSS keys off data-size-out on #buy), and its button says Sold out.
       The Tabby line follows the price: a quarter, fils kept. */
    const out = b.dataset.available !== 'true';
    const top = $('#buy');
    if (top) top.toggleAttribute('data-size-out', out);
    const label = cart && cart.querySelector('.pill-l[data-off]');
    if (label) label.textContent = out ? label.dataset.off : label.dataset.on;
    const tabby = $('#pdp-tabby');
    const part = $('#pdp-tabby-v');
    if (tabby) tabby.hidden = out;
    if (part) {
      const q = Math.round(price / 4);
      const label4 = (window.AZ && window.AZ.currency) || 'AED';
      part.textContent = label4 + ' ' + (q / 100).toLocaleString('en-US', { minimumFractionDigits: q % 100 ? 2 : 0, maximumFractionDigits: 2 });
    }
    const tamara = $('#pdp-tamara');
    if (tamara) {
      tamara.hidden = out;
      const widget = tamara.querySelector('tamara-widget');
      if (widget) {
        widget.setAttribute('amount', (price / 100).toFixed(2));
        if (window.TamaraWidgetV2) window.TamaraWidgetV2.refresh();
      }
    }
  });
}


/* ------------------------------------------------------------- layering

   A pair with no shared-notes line typed into the customiser works it out
   here: pull both descriptions, match them against the note vocabulary, and
   print the overlap. It means a new pair explains itself and nobody has to
   write that sentence again. */

const NOTES = ["agarwood", "almond", "amber", "ambergris", "ambroxan", "apple", "apricot", "aquatic", "basil", "benzoin", "bergamot", "caramel", "cardamom", "cashmeran", "cedar", "cherry", "chocolate", "cinnamon", "clove", "coconut", "coffee", "cypriol", "fig", "frankincense", "fresh", "geranium", "ginger", "grapefruit", "green", "honey", "incense", "iris", "jasmine", "labdanum", "lavender", "leather", "lemon", "lime", "mandarin", "marine", "mint", "musk", "myrrh", "neroli", "nutmeg", "oakmoss", "orange", "oud", "patchouli", "peach", "pear", "pepper", "petitgrain", "pineapple", "pistachio", "plum", "raspberry", "rose", "saffron", "sage", "salt", "sandalwood", "strawberry", "styrax", "suede", "tea", "thyme", "tobacco", "tonka", "tuberose", "vanilla", "vetiver", "violet", "ylang"];

function notesOf (product) {
  const text = ((product.body_html || product.description || '') + ' ' + product.title).toLowerCase();
  // Split into whole words rather than build a regex. The word-boundary
  // escape collapsed to a literal backspace character on the way through
  // the build, so this matched nothing at all and every pair stayed silent.
  const words = new Set(text.split(/[^a-z]+/));
  return NOTES.filter((n) => words.has(n));
}

async function initLayering () {
  const combos = $$('.combo[data-a][data-b]');
  if (!combos.length) return;

  /* v16: only the bottles that need a line worked out, and only those two
     bottles' own product files (a few KB each). It used to download the whole
     catalogue, 2.5 MB, on this page. */
  const open = combos.filter((c) => { const l = $('[data-shared]', c); return l && l.hidden; });
  if (!open.length) return;
  const handles = [...new Set(open.flatMap((c) => [c.dataset.a, c.dataset.b]))];
  const found = await Promise.all(handles.map((h) =>
    fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)));
  const byHandle = new Map(handles.map((h, n) => [h, found[n]]));

  open.forEach((c) => {
    const line = $('[data-shared]', c);
    if (!line || !line.hidden) return;       // merchant typed one; leave it
    const a = byHandle.get(c.dataset.a);
    const b = byHandle.get(c.dataset.b);
    if (!a || !b) return;
    const set = new Set(notesOf(b));
    const both = notesOf(a).filter((n) => set.has(n)).slice(0, 4);
    if (!both.length) return;
    const em = $('em', line);
    if (em) em.textContent = both.join(' · ');
    line.hidden = false;
  });
}

/* ------------------------------------------------------------- spotlight

   The slides are printed by Liquid with their discount on a data attribute.
   All this does is put the deepest first and run the crossfade, so the hero
   costs one sorted array rather than a catalogue download.

   One bottle evaporates while the next condenses out of the paper: the
   outgoing lifts and blurs away, the incoming is revealed through a gradient
   mask sweeping down, and the bottle in frame drifts the whole time it is
   there. A slideshow that sits motionless between transitions reads as
   waiting. */

function initSpotlight () {
  const host = $('#spotlight');
  if (!host) return;

  const slides = $$('.spot', host);
  if (!slides.length) { host.hidden = true; return; }

  /* Deepest discount leads — unless the hero names its bottles, in which
     case the order in the markup is a decision and re-sorting would quietly
     overrule it. */
  if (!host.hasAttribute('data-fixed')) {
    slides
      .slice()
      .sort((a, b) => Number(b.dataset.off || 0) - Number(a.dataset.off || 0))
      .forEach((el) => host.appendChild(el));
  }

  const order = $$('.spot', host);
  order.forEach((el, i) => {
    el.toggleAttribute('data-on', i === 0);
    el.tabIndex = i === 0 ? 0 : -1;
    if (i === 0) el.removeAttribute('aria-hidden');
    else el.setAttribute('aria-hidden', 'true');
  });

  if (order.length < 2) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let at = 0;
  let timer = null;

  const show = (n) => {
    const prev = order[at];
    at = n % order.length;
    const next = order[at];
    prev.removeAttribute('data-on');
    prev.setAttribute('data-out', '');
    prev.setAttribute('aria-hidden', 'true');
    prev.tabIndex = -1;
    setTimeout(() => prev.removeAttribute('data-out'), 1200);
    next.setAttribute('data-on', '');
    next.removeAttribute('aria-hidden');
    next.tabIndex = 0;
  };

  // never run the clock against a hidden document: browsers throttle those
  // timers to about once a minute and it only banks up a jump
  const start = () => { if (!document.hidden) timer = timer || setInterval(() => show(at + 1), 5200); };
  const stop = () => { clearInterval(timer); timer = null; };

  host.addEventListener('pointerenter', stop);
  host.addEventListener('pointerleave', start);
  host.addEventListener('focusin', stop);
  host.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
  start();
}

/* --------------------------------------------------------- sticky buy bar

   Shows only after the real Add to bag has left the screen. Two buy buttons
   visible at once is one buy button too many, and on a phone the decision is
   usually made three screens below the price. */

function initBuyBar () {
  const bar = $('#buybar');
  const anchorBtn = $('#pdp-cart');
  if (!bar || !anchorBtn) return;

  const io = new IntersectionObserver(([e]) => {
    bar.toggleAttribute('data-on', !e.isIntersecting && e.boundingClientRect.top < 0);
  }, { rootMargin: '0px' });
  io.observe(anchorBtn);
}

/* ------------------------------------------------------------- gift card

   Delivery is a choice, so only the fields that belong to it are shown and
   only those are required. Anything hidden is disabled as well as hidden:
   a hidden input still submits, and an empty Recipient email tells Shopify
   to email the card to nobody.

   The number is fixed to a UAE mobile. The +971 is printed beside the field
   rather than typed, the field accepts nine digits and nothing else, and the
   full number is composed into the hidden property on submit. Someone can
   neither paste an email into it nor send a half-finished number. */

/* --------------------------------------------------------- back to top

   Appears once the page has cost you something to scroll — a viewport and a
   half — and never on a page short enough not to need it.

   The listener is passive and does its work in a frame, because a scroll
   handler that reads layout on every event is the classic way to make a long
   list feel like it is dragging, which is the exact page this control exists
   for.

   Focus moves to the wordmark on arrival. Scrolling the window does not move
   the keyboard caret, so without this a keyboard user is returned to the top
   visually and left tabbing from the footer. */

function initToTop () {
  const btn = $('#to-top');
  if (!btn) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let ticking = false;
  let shown = false;

  const apply = () => {
    ticking = false;
    const past = scrollY > innerHeight * 1.5;
    const worth = document.documentElement.scrollHeight > innerHeight * 2.5;
    const want = past && worth;
    if (want === shown) return;
    shown = want;
    if (want) {
      btn.hidden = false;
      // a frame with display already set, so the transition has a start state
      requestAnimationFrame(() => btn.classList.add('is-in'));
    } else {
      btn.classList.remove('is-in');
      if (reduced) btn.hidden = true;
      else setTimeout(() => { if (!shown) btn.hidden = true; }, 450);
    }
  };

  addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  }, { passive: true });

  btn.addEventListener('click', () => {
    scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    const mark = $('.mark');
    if (mark) mark.focus({ preventScroll: true });
  });

  apply();
}

/* -------------------------------------------------------------- theme

   The head script already applied any stored choice before first paint; this
   only handles the tap. Three states, not two: with nothing stored the page
   follows the system, and the first tap has to move away from whatever the
   system is currently giving rather than from a hardcoded "light".

   The label is rewritten each time because the button carries no text — an
   icon-only control whose accessible name still says "switch to dark" after
   you have switched is worse than no label. */

function initTheme () {
  const btn = $('#theme-toggle');
  if (!btn) return;

  const systemDark = () => matchMedia('(prefers-color-scheme: dark)').matches;
  const current = () => document.documentElement.getAttribute('data-theme')
    || (systemDark() ? 'dark' : 'light');

  const label = () => {
    btn.setAttribute('aria-label', current() === 'dark' ? 'Switch to light' : 'Switch to dark');
  };

  btn.addEventListener('click', () => {
    const next = current() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('az-theme', next); } catch (e) {}
    label();
  });

  // a reader who never chose stays on the system, so follow it if it changes
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (!document.documentElement.getAttribute('data-theme')) label();
  });

  label();
}

/* ------------------------------------------------------- pair totals

   The "add both" line prints the two prices added up. Where a bottle has
   sizes the price moves with the select, so the figure has to follow it or
   it becomes a wrong number sitting next to a button — worse than no number.
   Prices ride on the options as data-price, so nothing is fetched. */

function initPairs () {
  const forms = $$('form[data-pair]');
  if (!forms.length) return;

  const paint = (form) => {
    const out = form.parentElement.querySelector('[data-pair-total]');
    if (!out) return;
    let total = 0;
    form.querySelectorAll('input[data-price]').forEach((i) => { total += Number(i.dataset.price) || 0; });
    form.querySelectorAll('select[data-pair-size]').forEach((sel) => {
      const opt = sel.selectedOptions[0];
      if (opt) total += Number(opt.dataset.price) || 0;
    });
    out.textContent = money(total);
  };

  forms.forEach((form) => {
    form.addEventListener('change', () => paint(form));
    paint(form);
  });
}

function initGift () {
  const form = $('[data-gift]');
  if (!form) return;

  const phone = $('#gift-phone', form);
  const phoneFull = $('#gift-phone-full', form);
  const auto = $('#gift-auto', form);

  const mode = () => (form.querySelector('[data-gift-mode]:checked') || {}).value || 'me';

  const apply = () => {
    const m = mode();
    $$('[data-gift-when]', form).forEach((el) => {
      const on = el.dataset.giftWhen.split(' ').includes(m);
      el.hidden = !on;
      el.querySelectorAll('input, textarea').forEach((f) => {
        f.disabled = !on;                       // hidden must also not submit
        if (f.type === 'tel' || f.type === 'email') f.required = on;
      });
    });
    // Shopify only auto-sends when it is an email address
    if (auto) auto.disabled = m !== 'email';
    if (auto) auto.value = m === 'email' ? 'on' : '';
    if (phoneFull) phoneFull.disabled = m !== 'whatsapp';
  };

  form.addEventListener('change', (e) => {
    if (e.target.matches('[data-gift-mode]')) apply();
  });

  /* --------------------------------------------------- what the amount buys

     Seven amounts and nothing to judge them against. Pricing real bottles
     against the chosen card turns an abstract number into a decision, and it
     surfaces the fact the amounts were hiding: the three smallest cards
     cannot buy anything in the shop on their own. */
  const panel = $('[data-gcard]', form);
  if (panel) {
    const items = $$('.gcard-item', panel);
    const floor = $('[data-gcard-floor]', panel);
    const shortTpl = panel.dataset.short || '[gap] short';

    const paint = () => {
      const picked = form.querySelector('input[name="id"]:checked');
      const amount = picked ? parseInt(picked.dataset.amount || '0', 10) : 0;
      let covered = 0;

      items.forEach((li) => {
        const price = parseInt(li.dataset.price || '0', 10);
        const ok = amount >= price;
        if (ok) covered += 1;
        li.dataset.covered = ok ? 'yes' : 'no';
        const tag = $('[data-gcard-state]', li);
        if (tag) {
          tag.textContent = ok
            ? (panel.dataset.covered || 'covered')
            : shortTpl.replace('[gap]', money(price - amount));
        }
      });

      // the honest line, shown only when the card really buys nothing
      if (floor) floor.hidden = covered > 0;
    };

    form.addEventListener('change', (e) => {
      if (e.target.name === 'id') paint();
    });
    paint();
  }

  if (phone) {
    // digits only, and never more than a UAE mobile has
    phone.addEventListener('input', () => {
      const clean = phone.value.replace(/\D/g, '').slice(0, 9);
      if (clean !== phone.value) phone.value = clean;
      if (phoneFull) phoneFull.value = clean ? '+971' + clean : '';
    });
  }

  /* ----------------------------------------------------------- validation

     The browser was doing this, and doing it in a bubble: a grey tooltip in
     the system font that vanishes on scroll, sits nowhere near the field on
     a phone, and says "Please match the requested format" about a number.
     novalidate is set from script, so anyone without JS keeps the native
     behaviour rather than losing validation altogether. */
  form.setAttribute('novalidate', '');

  const message = (f) => {
    if (f.validity.valueMissing) {
      if (f.type === 'tel') return 'Add their WhatsApp number.';
      if (f.type === 'email') return 'Add their email address.';
      return 'This one is needed.';
    }
    if (f.validity.typeMismatch && f.type === 'email') return 'That is not an email address.';
    if (f.validity.patternMismatch && f.type === 'tel') return 'A UAE mobile is nine digits starting with 5.';
    return 'Check this one.';
  };

  const clearError = (f) => {
    const field = f.closest('.gift-field');
    if (field) field.removeAttribute('data-invalid');
    f.removeAttribute('aria-invalid');
    const note = field && field.querySelector('.gift-err');
    if (note) note.remove();
  };

  const showError = (f) => {
    const field = f.closest('.gift-field');
    if (!field) return;
    clearError(f);
    field.setAttribute('data-invalid', '');
    f.setAttribute('aria-invalid', 'true');
    const note = document.createElement('p');
    note.className = 'gift-err';
    note.id = (f.id || f.name.replace(/\W+/g, '-')) + '-err';
    note.textContent = message(f);
    f.setAttribute('aria-describedby', note.id);
    field.appendChild(note);
  };

  // clearing as they fix it, rather than only on the next submit
  form.addEventListener('input', (e) => {
    if (e.target.matches('input, textarea') && e.target.validity.valid) clearError(e.target);
  });

  const validate = () => {
    const bad = [];
    $$('input, textarea', form).forEach((f) => {
      if (f.disabled || f.type === 'hidden') return;
      if (f.validity.valid) { clearError(f); return; }
      showError(f);
      bad.push(f);
    });
    return bad;
  };

  const done = $('#gift-done');

  form.addEventListener('submit', (e) => {
    if (phone && phoneFull) {
      const clean = phone.value.replace(/\D/g, '');
      phoneFull.value = clean ? '+971' + clean : '';
    }
    if (done) done.hidden = true;

    const bad = validate();
    if (bad.length) {
      /* Stop the delegated cart handler on document as well as the post.
         Without stopPropagation the bag would happily take a card addressed
         to nobody. */
      e.preventDefault();
      e.stopPropagation();
      bad[0].focus();
      bad[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    /* Optional fields left blank were still being submitted, so an order for
       someone who wrote no message arrived carrying an empty "Message"
       property — a blank line to read past on every one of them. Disabled
       inputs are not serialised, and apply() re-enables them on reset. */
    $$('[name^="properties["]', form).forEach((f) => {
      if (!f.disabled && !String(f.value).trim()) f.disabled = true;
    });
  });

  /* ------------------------------------------------------------- success

     The drawer opens on its own, but a line item cannot say who the card is
     for or when it goes, and those are the parts someone just typed and
     would like to see repeated back. */
  document.addEventListener('az:added', (e) => {
    if (e.detail.form !== form || !done) return;

    const m = mode();
    const amount = form.querySelector('[name=id]:checked');
    const label = amount ? amount.closest('.gift-chip').textContent.trim() : '';
    const who = (form.querySelector('[name="properties[Recipient name]"]') || {}).value || '';
    const when = (form.querySelector('[name="properties[Send on]"]') || {}).value || '';

    let where = 'It comes to you with the order.';
    if (m === 'whatsapp') where = 'We send the code to ' + (phoneFull.value || 'their number') + '.';
    if (m === 'email') where = 'It is emailed to them automatically.';

    /* The date field hands back 2026-09-12, which is a value, not a date
       anyone reads back to themselves. */
    let day = '';
    if (when) {
      const d = new Date(when + 'T00:00:00');
      if (!isNaN(d)) day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    }

    done.textContent = [
      label ? label + ' added.' : 'Added.',
      who ? 'For ' + who + '.' : '',
      where,
      day ? 'Going on ' + day + '.' : ''
    ].filter(Boolean).join(' ');
    done.hidden = false;

    form.reset();
    if (phoneFull) phoneFull.value = '';
    apply();
  });

  apply();
}

/* ---------------------------------------------------------------- start */

makeGrain();
/* ------------------------------------------------------------------- shelf

   A row that scrolls looks exactly like a row that does not. On a phone the
   shelf shows two bottles and an edge of a third, which reads as "we have
   three" rather than "there are twelve behind this".

   So the hint says how many, and the bar shows how far along you are. Both
   are driven from here rather than from a scroll-driven CSS animation: the
   animation version only ran on browsers that support scroll timelines and
   only when the visitor had not asked for reduced motion, so the people most
   likely to miss the affordance were the ones least likely to be shown it.
*/
/* The sort control submits on change, so choosing an option costs one tap
   instead of two. The form still works without this: the submit button is
   only hidden while JS is running, never removed. */
function initSort () {
  const sel = $('[data-sort]');
  if (!sel) return;
  const form = sel.form;
  if (form) form.classList.add('sortbar--live');
  sel.addEventListener('change', () => { if (form) form.submit(); });
}

function initRail () {
  const rail = $('#rail');
  if (!rail) return;
  const bar = $('.rail-bar i');
  const hint = $('#rail-hint');

  /* Telling somebody to scroll a row that already fits is worse than saying
     nothing, so the hint only exists while there is something to reach. */
  const fits = () => rail.scrollWidth <= rail.clientWidth + 4;

  const paint = () => {
    if (hint) hint.hidden = fits();
    if (fits()) { rail.removeAttribute('data-scrolled'); return; }

    const max = rail.scrollWidth - rail.clientWidth;
    const seen = (rail.scrollLeft + rail.clientWidth) / rail.scrollWidth;
    if (bar) bar.style.setProperty('--rail-progress', Math.max(0.08, Math.min(1, seen)).toFixed(3));

    // one nudge is enough; past this the row has explained itself
    if (rail.scrollLeft > 8) rail.setAttribute('data-scrolled', '');
    else rail.removeAttribute('data-scrolled');
  };

  rail.addEventListener('scroll', paint, { passive: true });
  addEventListener('resize', paint, { passive: true });
  paint();
}

initReveal();
initCart();
initVariants();
initBuyBar();
initLayering();
initSpotlight();
initGift();
initPairs();
initTheme();
initToTop();
initRail();
initSort();
